import { c as _c } from "react/compiler-runtime";
import { IcoClose16, IcoTextBlock16 } from '@onefootprint/icons';
import { IconButton, Shimmer, Tooltip } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import { useTranslation } from 'react-i18next';
import { getFileExtension, getFilePreviewType } from '../../../utils/file-preview-type';
import { countLines, isPastedText } from '../../../utils/pasted-text';
import AttachmentThumbnail from './attachment-thumbnail';
const actionButtonClassName = 'size-8 md:size-6 bg-neutral-inverted! hover:bg-none! ring-2 ring-(--background-color-primary) focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary';
const isPreviewableFile = (file: File) => getFilePreviewType(file.name, file.type) !== 'other';
type AttachedFileCardProps = {
  file: File;
  typeLabel: string;
  className?: string;
  previewable: boolean;
  isLoading?: boolean;
  onPreview: () => void;
  onRemove: () => void;
  onPasteInline?: () => void;
};
const AttachedFileCard = t0 => {
  const $ = _c(48);
  const {
    file,
    typeLabel,
    className,
    previewable,
    isLoading: t1,
    onPreview,
    onRemove,
    onPasteInline
  } = t0;
  const isLoading = t1 === undefined ? false : t1;
  const {
    t
  } = useTranslation("copilot");
  let t2;
  if ($[0] !== file.name || $[1] !== file.type) {
    t2 = getFilePreviewType(file.name, file.type);
    $[0] = file.name;
    $[1] = file.type;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const kind = t2;
  const isImage = kind === "image";
  let t3;
  if ($[3] !== file.name || $[4] !== kind) {
    t3 = getFileExtension(file.name).toUpperCase() || {
      image: "IMG",
      pdf: "PDF",
      text: "TXT",
      other: "FILE"
    }[kind];
    $[3] = file.name;
    $[4] = kind;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const extension = t3;
  let t4;
  if ($[6] !== file || $[7] !== t || $[8] !== typeLabel) {
    const lineCount = isPastedText(file) ? countLines(file.content) : null;
    t4 = lineCount == null ? typeLabel : lineCount === 1 ? t(_temp, {
      count: lineCount
    }) : t(_temp2, {
      count: lineCount
    });
    $[6] = file;
    $[7] = t;
    $[8] = typeLabel;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const detail = t4;
  let cardClassName;
  let t5;
  if ($[10] !== className || $[11] !== isImage || $[12] !== isLoading) {
    t5 = Symbol.for("react.early_return_sentinel");
    bb0: {
      cardClassName = cx("group/attachment relative h-32 shrink-0", isImage ? "w-32" : "w-44", className);
      if (isLoading) {
        let t6;
        if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
          t6 = <Shimmer className="min-h-0 flex-1" />;
          $[15] = t6;
        } else {
          t6 = $[15];
        }
        let t7;
        if ($[16] !== isImage) {
          t7 = !isImage && <div className="flex h-8 shrink-0 items-center gap-2 px-2.5"><Shimmer className="h-3 w-6 rounded" aria-hidden={true} /><Shimmer className="h-3 flex-1 rounded" aria-hidden={true} /></div>;
          $[16] = isImage;
          $[17] = t7;
        } else {
          t7 = $[17];
        }
        t5 = <div aria-busy="true" className={cx(cardClassName, "flex flex-col overflow-hidden rounded-lg border border-tertiary bg-primary")}>{t6}{t7}</div>;
        break bb0;
      }
    }
    $[10] = className;
    $[11] = isImage;
    $[12] = isLoading;
    $[13] = cardClassName;
    $[14] = t5;
  } else {
    cardClassName = $[13];
    t5 = $[14];
  }
  if (t5 !== Symbol.for("react.early_return_sentinel")) {
    return t5;
  }
  let t6;
  if ($[18] !== extension || $[19] !== file) {
    t6 = <div className="min-h-0 w-full flex-1 overflow-hidden" data-dd-privacy="mask"><AttachmentThumbnail file={file} extension={extension} /></div>;
    $[18] = extension;
    $[19] = file;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== extension || $[22] !== file.name || $[23] !== isImage) {
    t7 = !isImage && <div className="flex h-8 shrink-0 items-baseline gap-2 px-2.5 py-1.5"><span className="max-w-12 shrink-0 truncate text-caption-3 text-tertiary">{extension}</span><span className="min-w-0 truncate text-body-2 text-primary">{file.name}</span></div>;
    $[21] = extension;
    $[22] = file.name;
    $[23] = isImage;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  let t8;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div className="pointer-events-none absolute inset-0 bg-(--overlay-darken-2) opacity-0 transition-opacity duration-150 group-hover/attachment:opacity-100 group-focus-within/attachment:opacity-100" />;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] !== t6 || $[27] !== t7) {
    t9 = <>{t6}{t7}{t8}</>;
    $[26] = t6;
    $[27] = t7;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  const body = t9;
  const t10 = `${file.name} · ${detail}`;
  let t11;
  if ($[29] !== body || $[30] !== file.name || $[31] !== onPreview || $[32] !== previewable) {
    t11 = previewable ? <button type="button" aria-label={`Preview ${file.name}`} className="relative flex h-full w-full flex-col overflow-hidden rounded-lg border border-tertiary bg-primary text-left cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary" onClick={onPreview}>{body}</button> : <div className="relative flex h-full w-full flex-col overflow-hidden rounded-lg border border-tertiary bg-primary">{body}</div>;
    $[29] = body;
    $[30] = file.name;
    $[31] = onPreview;
    $[32] = previewable;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] !== onPasteInline || $[35] !== t) {
    t12 = onPasteInline && <Tooltip content={t(_temp3)}><IconButton aria-label={t(_temp4)} icon={IcoTextBlock16} color="quinary" rounded={true} size="tiny" className={actionButtonClassName} onClick={onPasteInline} /></Tooltip>;
    $[34] = onPasteInline;
    $[35] = t;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  const t13 = `Remove ${file.name}`;
  let t14;
  if ($[37] !== onRemove || $[38] !== t13) {
    t14 = <IconButton aria-label={t13} icon={IcoClose16} color="quinary" rounded={true} size="tiny" className={actionButtonClassName} onClick={onRemove} />;
    $[37] = onRemove;
    $[38] = t13;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  let t15;
  if ($[40] !== t12 || $[41] !== t14) {
    t15 = <div className="absolute -right-1 -top-1 flex gap-1 opacity-100 transition-opacity duration-150 [@media(hover:hover)_and_(pointer:fine)]:opacity-0 group-hover/attachment:opacity-100! group-focus-within/attachment:opacity-100!">{t12}{t14}</div>;
    $[40] = t12;
    $[41] = t14;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  let t16;
  if ($[43] !== cardClassName || $[44] !== t10 || $[45] !== t11 || $[46] !== t15) {
    t16 = <div className={cardClassName} title={t10}>{t11}{t15}</div>;
    $[43] = cardClassName;
    $[44] = t10;
    $[45] = t11;
    $[46] = t15;
    $[47] = t16;
  } else {
    t16 = $[47];
  }
  return t16;
};
export { AttachedFileCard, isPreviewableFile };
export default AttachedFileCard;
function _temp($) {
  return $.chat.pastedText.lineCount_one;
}
function _temp2($_0) {
  return $_0.chat.pastedText.lineCount_other;
}
function _temp3($_1) {
  return $_1.chat.pastedText.pasteInline;
}
function _temp4($_2) {
  return $_2.chat.pastedText.pasteInline;
}